/**
 * Documentos tributarios electrónicos ante el SII (DTE Fase C.5).
 *
 * Sólo se muestran en comités con el DTE encendido (`useEstadoDte`). Las
 * acciones vuelven a emitir en segundo plano: la pantalla refresca un par de
 * veces después de cada una para ver el resultado.
 */
import { useQuery } from "@tanstack/react-query";

import { apiRequest } from "../../lib/api";
import { getAuth, hasPermission } from "../../lib/auth";
import type { BadgeTone } from "../../components/ui";

export type EstadoDocumento =
  | "Borrador"
  | "Enviando"
  | "Incierto"
  | "Error"
  | "Emitido"
  | "Enviado_SII"
  | "Aceptado_SII"
  | "Rechazado_SII"
  | "Anulado"
  | "Reemplazado";

export interface EstadoDte {
  habilitado: boolean;
  por_estado: Partial<Record<EstadoDocumento, number>>;
}

export interface DocumentoTributario {
  id: number;
  tipo: string;
  codigo_sii: number;
  estado: EstadoDocumento;
  folio: number | null;
  numero_interno: string | null;
  numero_visible: string | null;
  lectura_id: number | null;
  periodo: string | null;
  socio_id: number | null;
  socio_nombre: string | null;
  rut_receptor: string;
  razon_social_receptor: string;
  monto_total: number;
  fecha_emision: string | null;
  documento_ref_id: number | null;
  folio_referencia: number | null;
  motivo_ref: string | null;
  respuesta_sii: string | null;
  track_id_sii: string | null;
  tiene_xml: boolean;
  tiene_pdf: boolean;
}

export interface PaginaDocumentos {
  items: DocumentoTributario[];
  total: number;
}

/** Cómo se dice cada estado en la oficina, y su color (design system §2.4). */
export const ESTADOS: Record<
  EstadoDocumento,
  { label: string; tono: BadgeTone; ayuda: string }
> = {
  Borrador: {
    label: "Por emitir",
    tono: "slate",
    ayuda: "Todavía no sale al SII.",
  },
  Enviando: {
    label: "Enviando",
    tono: "amber",
    ayuda: "Va en camino al proveedor.",
  },
  Incierto: {
    label: "Sin confirmar",
    tono: "amber",
    ayuda:
      "Se envió y no hubo respuesta: puede que tenga folio. No la vuelvas a emitir sin revisar.",
  },
  Error: {
    label: "Con error",
    tono: "rose",
    ayuda:
      "El proveedor la rechazó sin gastar folio: corrige los datos y reintenta.",
  },
  Emitido: {
    label: "Emitida",
    tono: "brand",
    ayuda: "Tiene folio; falta que el SII la acepte.",
  },
  Enviado_SII: {
    label: "En el SII",
    tono: "brand",
    ayuda: "El SII la está revisando.",
  },
  Aceptado_SII: {
    label: "Aceptada",
    tono: "emerald",
    ayuda: "El SII la aceptó.",
  },
  Rechazado_SII: {
    label: "Rechazada por el SII",
    tono: "rose",
    ayuda: "Sigue siendo deuda del socio: hay que re-emitirla con folio nuevo.",
  },
  Anulado: { label: "Anulada", tono: "slate", ayuda: "Sin efecto." },
  Reemplazado: {
    label: "Reemplazada",
    tono: "slate",
    ayuda: "Rechazada por el SII y re-emitida: queda como registro del folio.",
  },
};

/** Estados que alguien tiene que resolver, en el orden en que conviene mirarlos. */
export const ESTADOS_CON_PROBLEMA: EstadoDocumento[] = [
  "Rechazado_SII",
  "Incierto",
  "Error",
];

export const ORDEN_ESTADOS: EstadoDocumento[] = [
  "Rechazado_SII",
  "Incierto",
  "Error",
  "Borrador",
  "Enviando",
  "Emitido",
  "Enviado_SII",
  "Aceptado_SII",
  "Reemplazado",
  "Anulado",
];

const TIPOS: Record<string, string> = {
  BoletaExenta: "Boleta exenta",
  Boleta: "Boleta",
  Factura: "Factura",
  FacturaExenta: "Factura exenta",
  NotaCredito: "Nota de crédito",
  NotaDebito: "Nota de débito",
};

export const tipoLabel = (tipo: string): string => TIPOS[tipo] ?? tipo;

export function obtenerEstadoDte(cierreId?: number): Promise<EstadoDte> {
  const q = cierreId != null ? `?cierre_id=${cierreId}` : "";
  return apiRequest<EstadoDte>(`/api/v1/documentos-tributarios/estado${q}`);
}

/** ¿El comité emite DTE? Se consulta una vez (el menú y el cierre lo usan). */
export function useEstadoDte(cierreId?: number) {
  const auth = getAuth();
  return useQuery({
    queryKey: ["dte", "estado", cierreId ?? null],
    queryFn: () => obtenerEstadoDte(cierreId),
    enabled: auth != null && hasPermission(auth, "cierres.read"),
    staleTime: cierreId != null ? 0 : 5 * 60_000,
  });
}

export function listarDocumentos(filtros: {
  estado?: string | undefined;
  cierreId?: number | undefined;
  limit: number;
  offset: number;
}): Promise<PaginaDocumentos> {
  const p = new URLSearchParams({
    limit: String(filtros.limit),
    offset: String(filtros.offset),
  });
  if (filtros.estado) p.set("estado", filtros.estado);
  if (filtros.cierreId != null) p.set("cierre_id", String(filtros.cierreId));
  return apiRequest<PaginaDocumentos>(`/api/v1/documentos-tributarios?${p}`);
}

export type AccionDte =
  | "reintentar"
  | "conciliar"
  | "reemitir-incierto"
  | "reemitir";

export function accionDocumento(
  id: number,
  accion: AccionDte,
  motivo: string,
): Promise<DocumentoTributario> {
  return apiRequest<DocumentoTributario>(
    `/api/v1/documentos-tributarios/${id}/${accion}`,
    {
      method: "POST",
      body: { motivo },
    },
  );
}

export function confirmarFolio(
  id: number,
  folio: number,
  motivo: string,
): Promise<DocumentoTributario> {
  return apiRequest<DocumentoTributario>(
    `/api/v1/documentos-tributarios/${id}/confirmar-folio`,
    { method: "POST", body: { motivo, folio } },
  );
}
